import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, X, Save, Image as ImageIcon, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { createProperty, uploadImages } from '../services/api';

const PropertyForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    tipo: 'venta',
    tipoPropiedad: 'Casa',
    precio: '',
    direccion: '',
    colonia: '',
    ciudad: 'Playa del Carmen',
    estado: 'Quintana Roo',
    codigoPostal: '',
    habitaciones: 0,
    banosCompletos: 0,
    mediosBanos: 0,
    estacionamientos: 0,
    areaTotal: 0,
    areaConstruida: 0,
    niveles: 1,
    anioConstruccion: '',
    amenidades: [],
    caracteristicasEspeciales: [],
    servicios: {
      agua: true,
      luz: true,
      gas: false,
      internet: false
    },
    estatus: 'disponible'
  });

  const tiposPropiedad = ['Casa', 'Departamento', 'Terreno', 'Local Comercial', 'Oficina'];
  const ciudadesQuintanaRoo = ['Playa del Carmen', 'Cancún', 'Tulum', 'Cozumel', 'Chetumal', 'Puerto Morelos', 'Isla Mujeres'];
  
  const amenidadesComunes = [
    'Alberca', 'Gimnasio', 'Seguridad 24/7', 'Jardín', 'Terraza', 'Roof Garden',
    'Elevador', 'Estacionamiento Techado', 'Cuarto de Servicio', 'Sala de Juegos',
    'Salón de Eventos', 'Área de BBQ', 'Pet Friendly', 'Amueblado'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name.startsWith('servicio_')) {
      const serviceName = name.replace('servicio_', '');
      setFormData({
        ...formData,
        servicios: {
          ...formData.servicios,
          [serviceName]: checked
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleAmenidadToggle = (amenidad) => {
    setFormData({
      ...formData,
      amenidades: formData.amenidades.includes(amenidad)
        ? formData.amenidades.filter(a => a !== amenidad)
        : [...formData.amenidades, amenidad]
    });
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (selectedImages.length + files.length > 30) {
      alert('Máximo 30 fotos por propiedad');
      return;
    }
    setSelectedImages([...selectedImages, ...files]);
  };

  const handleVideoSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedVideos([...selectedVideos, ...files]);
  };

  const removeImage = (index) => {
    setSelectedImages(selectedImages.filter((_, i) => i !== index));
  };

  const removeVideo = (index) => {
    setSelectedVideos(selectedVideos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Subir imágenes primero
      let imageUrls = [];
      if (selectedImages.length > 0) {
        const uploadResult = await uploadImages(selectedImages, true);
        imageUrls = uploadResult.uploaded.map(img => img.url);
      }

      // Por ahora, los videos se manejarán como URLs (puedes expandir esto después)
      const videoUrls = selectedVideos.map(v => URL.createObjectURL(v));

      // Crear propiedad con las URLs de imágenes
      const propertyData = {
        ...formData,
        precio: parseFloat(formData.precio),
        habitaciones: parseInt(formData.habitaciones) || 0,
        banosCompletos: parseInt(formData.banosCompletos) || 0,
        mediosBanos: parseInt(formData.mediosBanos) || 0,
        estacionamientos: parseInt(formData.estacionamientos) || 0,
        areaTotal: parseFloat(formData.areaTotal) || 0,
        areaConstruida: parseFloat(formData.areaConstruida) || 0,
        niveles: parseInt(formData.niveles) || 1,
        fotos: imageUrls.length > 0 ? imageUrls : ['https://via.placeholder.com/800x600?text=Sin+Imagen'],
        videos: videoUrls
      };

      await createProperty(propertyData);
      alert('¡Propiedad creada exitosamente!');
      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Error creando propiedad:', err);
      setError(err.response?.data?.detail || 'Error al crear la propiedad. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="outline"
            className="mb-4"
          >
            <ArrowLeft size={18} className="mr-2" />
            Volver al Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-blue-900 font-heading">Nueva Propiedad</h1>
          <p className="text-gray-600 font-body">Agrega una nueva propiedad al portafolio</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 font-body">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información Básica */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 font-heading">Información Básica</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Título de la Propiedad *
                </label>
                <input
                  type="text"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                  placeholder="Ej: Casa Residencial Premium en Zona Norte"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Tipo de Operación *
                </label>
                <select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                >
                  <option value="venta">Venta</option>
                  <option value="renta">Renta</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Tipo de Propiedad *
                </label>
                <select
                  name="tipoPropiedad"
                  value={formData.tipoPropiedad}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                >
                  {tiposPropiedad.map(tipo => (
                    <option key={tipo} value={tipo}>{tipo}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Precio (MXN) *
                </label>
                <input
                  type="number"
                  name="precio"
                  value={formData.precio}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="1000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Estatus
                </label>
                <select
                  name="estatus"
                  value={formData.estatus}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                >
                  <option value="disponible">Disponible</option>
                  <option value="vendida">Vendida</option>
                  <option value="rentada">Rentada</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Descripción *
                </label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none resize-none font-body"
                  placeholder="Describe la propiedad..."
                />
              </div>
            </div>
          </div>

          {/* Ubicación */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 font-heading">Ubicación</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Dirección *
                </label>
                <input
                  type="text"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                  placeholder="Calle y número"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Colonia *
                </label>
                <input
                  type="text"
                  name="colonia"
                  value={formData.colonia}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                  placeholder="Nombre de la colonia"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Ciudad *
                </label>
                <select
                  name="ciudad"
                  value={formData.ciudad}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                >
                  {ciudadesQuintanaRoo.map(ciudad => (
                    <option key={ciudad} value={ciudad}>{ciudad}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Estado
                </label>
                <input
                  type="text"
                  name="estado"
                  value={formData.estado}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 font-body"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Código Postal
                </label>
                <input
                  type="text"
                  name="codigoPostal"
                  value={formData.codigoPostal}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                  placeholder="77710"
                />
              </div>
            </div>
          </div>

          {/* Características */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 font-heading">Características</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Habitaciones
                </label>
                <input
                  type="number"
                  name="habitaciones"
                  value={formData.habitaciones}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Baños Completos
                </label>
                <input
                  type="number"
                  name="banosCompletos"
                  value={formData.banosCompletos}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Medios Baños
                </label>
                <input
                  type="number"
                  name="mediosBanos"
                  value={formData.mediosBanos}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Estacionamientos
                </label>
                <input
                  type="number"
                  name="estacionamientos"
                  value={formData.estacionamientos}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Área Total (m²) *
                </label>
                <input
                  type="number"
                  name="areaTotal"
                  value={formData.areaTotal}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Área Construida (m²)
                </label>
                <input
                  type="number"
                  name="areaConstruida"
                  value={formData.areaConstruida}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Niveles
                </label>
                <input
                  type="number"
                  name="niveles"
                  value={formData.niveles}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Año de Construcción
                </label>
                <input
                  type="text"
                  name="anioConstruccion"
                  value={formData.anioConstruccion}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none font-body"
                  placeholder="2024"
                />
              </div>
            </div>
          </div>

          {/* Amenidades */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 font-heading">Amenidades</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {amenidadesComunes.map(amenidad => (
                <label key={amenidad} className="flex items-center gap-2 cursor-pointer font-body">
                  <input
                    type="checkbox"
                    checked={formData.amenidades.includes(amenidad)}
                    onChange={() => handleAmenidadToggle(amenidad)}
                    className="w-4 h-4 text-blue-900 border-gray-300 rounded focus:ring-blue-900"
                  />
                  <span className="text-sm text-gray-700">{amenidad}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Servicios */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 font-heading">Servicios</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <label className="flex items-center gap-2 cursor-pointer font-body">
                <input
                  type="checkbox"
                  name="servicio_agua"
                  checked={formData.servicios.agua}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-900 border-gray-300 rounded focus:ring-blue-900"
                />
                <span className="text-sm text-gray-700">Agua</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer font-body">
                <input
                  type="checkbox"
                  name="servicio_luz"
                  checked={formData.servicios.luz}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-900 border-gray-300 rounded focus:ring-blue-900"
                />
                <span className="text-sm text-gray-700">Luz</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer font-body">
                <input
                  type="checkbox"
                  name="servicio_gas"
                  checked={formData.servicios.gas}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-900 border-gray-300 rounded focus:ring-blue-900"
                />
                <span className="text-sm text-gray-700">Gas</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer font-body">
                <input
                  type="checkbox"
                  name="servicio_internet"
                  checked={formData.servicios.internet}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-900 border-gray-300 rounded focus:ring-blue-900"
                />
                <span className="text-sm text-gray-700">Internet</span>
              </label>
            </div>
          </div>

          {/* Fotos y Videos */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 font-heading">Fotos y Videos</h2>
            
            {/* Fotos */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                Fotos (máximo 30) *
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                  id="image-upload"
                />
                <label htmlFor="image-upload" className="cursor-pointer">
                  <ImageIcon className="mx-auto text-gray-400 mb-2" size={48} />
                  <p className="text-sm text-gray-600 font-body">
                    Haz clic para seleccionar fotos o arrastra aquí
                  </p>
                  <p className="text-xs text-gray-500 mt-1 font-body">
                    Se aplicará watermark automáticamente
                  </p>
                </label>
              </div>

              {/* Preview de imágenes */}
              {selectedImages.length > 0 && (
                <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-3">
                  {selectedImages.map((file, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Videos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                Videos (opcional)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={handleVideoSelect}
                  className="hidden"
                  id="video-upload"
                />
                <label htmlFor="video-upload" className="cursor-pointer">
                  <Video className="mx-auto text-gray-400 mb-2" size={48} />
                  <p className="text-sm text-gray-600 font-body">
                    Haz clic para seleccionar videos
                  </p>
                </label>
              </div>

              {/* Lista de videos */}
              {selectedVideos.length > 0 && (
                <div className="mt-4 space-y-2">
                  {selectedVideos.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                      <span className="text-sm text-gray-700 font-body">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeVideo(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex gap-4 justify-end">
            <Button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              disabled={loading}
              className="font-body"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-body"
            >
              {loading ? (
                <>Guardando...</>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Guardar Propiedad
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyForm;
